import { Autocomplete, Grid, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  AdviserItem,
  AfslItem,
  GroupItem,
  ClientAccountTypeForSearch,
  ClientSearchParameters,
  PracticeItem,
  ServiceType,
} from '../../store/types';
import { EnumerationMultiSelect, ToggleButtonItem, WO2ToggleButtonGroup } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import { Props } from '../../container';
import { ClientItem } from 'src/features/clients/list/store/types';
import { useDebounce } from 'src/common/hooks';
import { PagedRequest } from 'src/store';

const serviceTypeToggleButtons: ToggleButtonItem<string>[] = [
  { name: 'All', value: '' },
  { name: ServiceType.Mda.displayName, value: ServiceType.Mda.name },
  { name: ServiceType.Roa.displayName, value: ServiceType.Roa.name },
];

export const ReportingGenerationForm = ({
  bulk,
  setServiceTypeParameter,
  setClientTypesParameter,
  afslsProgress,
  fetchClientList,
  setSelectedAfsls,
  setSelectedGroups,
  setSelectedClients,
  setSelectedPractices,
  setSelectedAdvisers,
  clientListProgress,
  discardCsvUpload,
  fetchAfsls,
  fetchFilteredClients,
  isDataFromTriumph,
}: Props) => {
  const [clientSearch, setClientSearch] = useState<string>('');
  const clientDebounceSearch = useDebounce<string>(clientSearch, 500);

  useEffect(() => {
    const pagination: PagedRequest = {
      pageNumber: 1,
      pageSize: 100,
      queryFields: [],
    };
    fetchClientList({
      pagination: pagination,
      inputs: {
        pagination: pagination,
        clientNameSearch: clientDebounceSearch,
      },
    });
  }, [clientDebounceSearch]);

  useEffect(() => {
    fetchAfsls();
  }, []);

  const handleClientSearch = () => {
    const request: ClientSearchParameters = {
      afsls: bulk.selectedAfsls,
      practices: bulk.selectedPractices,
      advisers: bulk.selectedAdvisers,
      clientTypes: bulk.clientTypes,
      serviceType: bulk.serviceType,
      statuses: [],
      securityHeld: bulk.securityHeld,
      portfolioValueFrom: bulk.portfolioValueFrom || undefined,
      portfolioValueTo: bulk.portfolioValueTo || undefined,
      availableCashBalanceFrom: bulk.availableCashBalanceFrom || undefined,
      availableCashBalanceTo: bulk.availableCashBalanceTo || undefined,
      modelId: bulk.model?.modelId,
      securityId: bulk.security?.securityId,
      isDataFromTriumph: isDataFromTriumph,
    };
    discardCsvUpload();
    fetchFilteredClients(request);
  };

  return (
    <Grid item container direction="column" xs={12} sm={4}>
      <Grid item>
        <Autocomplete
          noOptionsText="No items to select"
          id="afsl-multiselect"
          multiple
          fullWidth
          disabled={bulk.csvUploaded || bulk.selectedClients.length > 0 || bulk.selectedGroups.length > 0}
          options={bulk.afsls}
          loading={afslsProgress.isLoading}
          value={bulk.selectedAfsls}
          getOptionLabel={(option: AfslItem) => option.afslName}
          renderInput={(params) => <TextField {...params} margin="normal" label="AFSL" fullWidth />}
          onChange={(_e: React.SyntheticEvent<Element, Event>, values: AfslItem[]) => {
            setSelectedAfsls(values);
          }}
          isOptionEqualToValue={(option: AfslItem, value: AfslItem) => option.afslId === value.afslId}
        />
      </Grid>
      <Grid item>
        <Autocomplete
          noOptionsText="No items to select"
          id="practice-multiselect"
          fullWidth
          multiple
          options={bulk.practices}
          value={bulk.selectedPractices}
          getOptionLabel={(option: PracticeItem) => option.practiceName}
          renderInput={(params) => <TextField {...params} margin="normal" label="Practice" fullWidth />}
          onChange={(_e: React.SyntheticEvent<Element, Event>, values: PracticeItem[]) => {
            setSelectedPractices(values);
          }}
          isOptionEqualToValue={(option: PracticeItem, value: PracticeItem) => option.practiceId === value.practiceId}
        />
      </Grid>
      <Grid item>
        <Autocomplete
          noOptionsText="No items to select"
          id="adviser-multiselect"
          fullWidth
          multiple
          options={bulk.advisers}
          value={bulk.selectedAdvisers}
          getOptionLabel={(option: AdviserItem) => option.advisorName}
          renderInput={(params) => <TextField {...params} margin="normal" label="Adviser" fullWidth />}
          onChange={(_e: React.SyntheticEvent<Element, Event>, values: AdviserItem[]) => {
            setSelectedAdvisers(values);
          }}
          isOptionEqualToValue={(option: AdviserItem, value: AdviserItem) => option.advisorId === value.advisorId}
        />
      </Grid>
      <Grid item>
        <EnumerationMultiSelect
          label="Account Type"
          type={ClientAccountTypeForSearch}
          value={bulk.clientTypes}
          onChange={setClientTypesParameter}
        />
      </Grid>
      <Grid item container direction="column" spacing={1} mt={1}>
        <Grid item>
          <Typography variant="h4">Service Type</Typography>
        </Grid>
        <Grid item>
          <WO2ToggleButtonGroup
            fullWidth={true}
            buttons={serviceTypeToggleButtons}
            value={bulk.serviceType}
            onChangeHandler={setServiceTypeParameter}
          />
        </Grid>
      </Grid>
      <Grid item>
        <Autocomplete
          noOptionsText="No items to select"
          id="group-multiselect"
          multiple
          fullWidth
          disabled={bulk.csvUploaded}
          options={bulk.groups}
          value={bulk.selectedGroups}
          getOptionLabel={(option: GroupItem) => `${option.groupName} (${option.groupId})`}
          renderInput={(params) => <TextField {...params} margin="normal" label="Group Search" fullWidth />}
          onChange={(_e: React.SyntheticEvent<Element, Event>, values: GroupItem[]) => {
            setSelectedGroups(values);
          }}
          isOptionEqualToValue={(option: GroupItem, value: GroupItem) => option.groupId === value.groupId}
        />
      </Grid>
      <Grid item>
        <Autocomplete
          noOptionsText="No items to select"
          id="client-multiselect"
          multiple
          fullWidth
          disabled={bulk.csvUploaded}
          options={bulk.clients}
          value={bulk.selectedClients}
          getOptionLabel={(option: ClientItem) => `${option.accountName} (${option.clientId})`}
          loading={clientListProgress.isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={(e) => setClientSearch(e.target.value)}
              margin="normal"
              label="Client Search"
              fullWidth
            />
          )}
          onChange={(_e: React.SyntheticEvent<Element, Event>, values: ClientItem[]) => {
            setSelectedClients(values);
          }}
          isOptionEqualToValue={(option: ClientItem, value: ClientItem) => option.clientId === value.clientId}
        />
      </Grid>

      <Grid item alignSelf="flex-end" mt={1}>
        <WO2Button onClick={handleClientSearch} variant="contained">
          Search
        </WO2Button>
      </Grid>
    </Grid>
  );
};
